import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DataTypeName } from "../../../../features/data/data-types";
import { CsvRecord, MetaRecord } from "../../../../features/import/csv";

// The file writes a path as "A/B" or "A|B"; the analysis lists it as "A / B".
const collectionKey = (name: string) =>
  name
    .split(/[/|]/)
    .map((n) => n.trim())
    .join(" / ")
    .toLowerCase();

export const ImportCsvPreview = ({
  records,
  newCollections,
}: {
  records: CsvRecord[];
  /** The collections the file adds, with the user's ticks. One that is not ticked is not created. */
  newCollections?: MetaRecord[];
}) => {
  const [previewRecord, setPreviewRecord] = useState<CsvRecord>();
  const [previewIndex, setPreviewIndex] = useState(-1);

  const preview = useCallback(
    (dir: number) => {
      const index = previewIndex + dir;
      setPreviewIndex(index);
      setPreviewRecord(records[index]);
    },
    [previewIndex, records]
  );

  useEffect(() => {
    if (records?.length > 0) {
      setPreviewIndex(0);
      setPreviewRecord(records[0]);
    } else {
      setPreviewRecord(null);
    }
  }, [records]);

  const subRecords = useMemo(() => {
    if (!previewRecord) return [];
    const sr = previewRecord.subRecords.map((r) => ({
      ...r,
      fields: r.fields.map((f, i) => ({
        ...f,
        ignore: previewRecord.fields[i].ignore,
      })),
    }));
    return previewRecord.subRecords.length > 0 ||
      previewRecord.fields.some((f) => f.isVariant)
      ? [previewRecord, ...sr]
      : [];
  }, [previewRecord]);

  if (records?.length === 0 || !previewRecord)
    return <Box>No records found</Box>;

  // The collections this product ends up in: the ones it names, minus new ones left unticked.
  const collections = [
    ...new Map(
      (previewRecord.collectionNames ?? []).map((name) => {
        const key = collectionKey(name);
        const added = newCollections?.find(
          (c) => c.name.toLowerCase() === key,
        );
        return [key, { name, isNew: !!added, skipped: !!added && !added.checked }];
      }),
    ).values(),
  ].filter((c) => !c.skipped);

  return (
    <Box
      sx={{
        "& td img": {
          maxHeight: "100%",
          maxWidth: 100,
          borderRadius: 0.5,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6" my={1}>
          Preview
        </Typography>
        <Box>
          <IconButton disabled={previewIndex <= 0} onClick={() => preview(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            disabled={previewIndex >= (records?.length ?? 0) - 1}
            onClick={() => preview(1)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>Field</TableCell>
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell>{previewRecord.categoryName}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>{previewRecord.code} </TableCell>
          </TableRow>
          {previewRecord.name && (
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>{previewRecord.name}</TableCell>
            </TableRow>
          )}
          {collections.length > 0 && (
            <TableRow>
              <TableCell>Collections</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {collections.map((c) => (
                    <Chip
                      key={c.name}
                      size="small"
                      label={c.isNew ? `${c.name} · new` : c.name}
                      color={c.isNew ? "primary" : "default"}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          )}
          {previewRecord.fields
            .filter(
              (f) =>
                !f.ignore &&
                !f.isVariant &&
                // Shown above, as the name the product will get.
                !(previewRecord.name && f.name.toLowerCase() === "name"),
            )
            .map((f, i) => (
              <TableRow key={i}>
                <TableCell component="th">{f.name}</TableCell>
                <TableCell>
                  {f.dataType === DataTypeName.Image &&
                    f.value?.indexOf("http") === 0 && (
                      <div>
                        <img src={f.value} alt={f.name} />
                      </div>
                    )}{" "}
                  {f.value}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {subRecords.length > 0 && (
        <Box mt={2}>
          <Typography variant="h6" my={1}>
            Variants
          </Typography>
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {subRecords[0].fields
                    .filter((f) => !f.ignore && f.isVariant)
                    .map((f, i) => (
                      <TableCell key={i}>{f.name}</TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {subRecords.map((r) => (
                  <TableRow key={r.index}>
                    {r.fields
                      .filter((f) => !f.ignore && f.isVariant)
                      .map((f, i) => (
                        <TableCell key={i}>
                          {f.dataType === DataTypeName.Image &&
                            f.value?.indexOf("http") === 0 && (
                              <div>
                                <img src={f.value} alt={f.name} />
                              </div>
                            )}
                          {f.value}
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};
